import React, { useState } from 'react';
import { MapPinOff, ShieldCheck, Info, Loader2 } from 'lucide-react';
import AddressAutocomplete from '../AddressAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PropertyStep = ({ config, draft, updateDraft, nextStep, prevStep }) => {
  const [outOfArea, setOutOfArea] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [lookup, setLookup] = useState('idle');
  const [lookupData, setLookupData] = useState(null);
  const [showDispute, setShowDispute] = useState(false);

  const service = config.services.find((s) => s.key === draft.service_type);
  const needsSqft = service?.pricing === 'sqft';

  const sqftLocked = lookup === 'found' && lookupData?.sqft_tier != null;
  const bedsLocked = lookup === 'found' && lookupData?.bedrooms != null;
  const bathsLocked = lookup === 'found' && lookupData?.bathrooms != null;

  const checkZip = async (zip) => {
    if (!zip) {
      setOutOfArea(false);
      return true;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/service-area/${zip}`);
      const data = await res.json();
      setOutOfArea(!data.serviced);
      if (!data.serviced) setShowModal(true);
      return data.serviced;
    } catch {
      return true;
    }
  };

  const lookupProperty = async (address) => {
    setLookup('loading');
    setShowDispute(false);
    try {
      const res = await fetch(`${BACKEND_URL}/api/property-lookup?address=${encodeURIComponent(address)}`);
      const data = await res.json();
      if (data.found) {
        setLookupData(data);
        setLookup('found');
        const updates = { property_verified: true };
        if (data.sqft_tier != null) updates.square_feet = data.sqft_tier;
        if (data.bedrooms != null) updates.bedrooms = data.bedrooms;
        if (data.bathrooms != null) updates.bathrooms = data.bathrooms;
        updateDraft(updates);
      } else {
        setLookupData(null);
        setLookup('not_found');
        updateDraft({ property_verified: false });
      }
    } catch {
      setLookupData(null);
      setLookup('not_found');
      updateDraft({ property_verified: false });
    }
  };

  const handleAddress = (address, zip, fromPlace) => {
    updateDraft({ address, zip_code: zip, property_verified: false, property_details_note: '' });
    if (zip && zip !== draft.zip_code) checkZip(zip);
    if (!zip) setOutOfArea(false);
    if (fromPlace && address) {
      lookupProperty(address);
    } else if (lookup !== 'idle') {
      setLookup('idle');
      setLookupData(null);
      setShowDispute(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lookup === 'loading') return;
    if (draft.zip_code) {
      const ok = await checkZip(draft.zip_code);
      if (!ok) return;
    }
    nextStep();
  };

  const verifiedSummary = lookupData
    ? [
        lookupData.bedrooms != null ? `${lookupData.bedrooms === 0 ? 'Studio' : `${lookupData.bedrooms} bed`}` : null,
        lookupData.bathrooms != null ? `${lookupData.bathrooms} bath` : null,
        lookupData.square_feet != null ? `${lookupData.square_feet.toLocaleString()} sqft` : null,
      ].filter(Boolean).join(' · ')
    : '';

  return (
    <form onSubmit={handleSubmit} className="form-step" data-testid="property-step">
      <h2 className="step-title">Property Details</h2>
      <p className="step-description">Tell us about the property so we can price it accurately</p>

      <div className="form-group">
        <label className="form-label">Property Address</label>
        <AddressAutocomplete
          className="form-input"
          value={draft.address}
          onChange={handleAddress}
          placeholder="Start typing the address..."
          required
          data-testid="address-input"
        />
        {outOfArea && (
          <p className="form-error" data-testid="out-of-area-inline">
            This address is outside our current service area.
          </p>
        )}
      </div>

      {lookup === 'loading' && (
        <div className="property-banner loading" data-testid="property-lookup-loading">
          <Loader2 size={18} className="spin" />
          <span>Verifying property details from public records…</span>
        </div>
      )}
      {lookup === 'found' && (
        <div className="property-banner verified" data-testid="property-verified-banner">
          <ShieldCheck size={18} />
          <div>
            <strong>Property verified from public records</strong>
            {verifiedSummary && <span>{verifiedSummary}</span>}
          </div>
        </div>
      )}
      {lookup === 'not_found' && (
        <div className="property-banner notfound" data-testid="property-not-found-banner">
          <Info size={18} />
          <span>
            We couldn't find this property in public records. Please enter the details
            below — they're subject to verification before your cleaning.
          </span>
        </div>
      )}

      {needsSqft && (
        <div className="form-group">
          <label className="form-label">Approximate Square Footage</label>
          <select
            className="form-input"
            value={draft.square_feet}
            onChange={(e) => updateDraft({ square_feet: e.target.value })}
            required
            disabled={sqftLocked}
            data-testid="sqft-select"
          >
            <option value="">Select size...</option>
            {config.sqft_options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Bedrooms</label>
          <select
            className="form-input"
            value={draft.bedrooms}
            onChange={(e) => updateDraft({ bedrooms: parseInt(e.target.value) })}
            disabled={bedsLocked}
            data-testid="bedrooms-select"
          >
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
              <option key={n} value={n}>{n === 0 ? 'Studio' : n}{n === 10 ? '+' : ''}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Bathrooms</label>
          <select
            className="form-input"
            value={draft.bathrooms}
            onChange={(e) => updateDraft({ bathrooms: parseFloat(e.target.value) })}
            disabled={bathsLocked}
            data-testid="bathrooms-select"
          >
            {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 6, 7, 8, 9, 10].map((n) => (
              <option key={n} value={n}>{n}{n === 10 ? '+' : ''}</option>
            ))}
          </select>
        </div>
      </div>

      {lookup === 'found' && (
        <div className="property-dispute">
          <button
            type="button"
            className="dispute-link"
            onClick={() => setShowDispute(!showDispute)}
            data-testid="property-dispute-link"
          >
            Property details incorrect?
          </button>
          {showDispute && (
            <textarea
              className="form-input dispute-note"
              rows={3}
              placeholder="Tell us what's different (e.g., converted garage, new ADU, recent addition). Our team will review it before your cleaning."
              value={draft.property_details_note || ''}
              onChange={(e) => updateDraft({ property_details_note: e.target.value })}
              data-testid="property-dispute-note"
            />
          )}
        </div>
      )}

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={prevStep} data-testid="property-step-back">
          Back
        </button>
        <button type="submit" className="btn btn-primary" disabled={outOfArea || lookup === 'loading'} data-testid="property-step-next">
          Continue
        </button>
      </div>

      {showModal && (
        <div className="wizard-modal-overlay" data-testid="out-of-area-modal">
          <div className="wizard-modal">
            <div className="wizard-modal-icon"><MapPinOff size={32} strokeWidth={1.5} /></div>
            <h3>Outside Our Service Area</h3>
            <p>
              We're sorry — we don't currently service this area. We proudly serve
              Los Angeles and surrounding areas. Feel free to contact us and we'll
              try to accommodate when possible.
            </p>
            <div className="wizard-modal-actions">
              <a href="/contact" className="btn btn-secondary">Contact Us</a>
              <button type="button" className="btn btn-primary" onClick={() => setShowModal(false)} data-testid="out-of-area-close">
                Try Another Address
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default PropertyStep;
